import { useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';
import AttendanceList from 'src/components/Attendance/AttendanceList';
import AttendanceLog from 'src/components/Attendance/AttendanceLog';
import { isPrimaryAdvisor } from 'src/components/SigningForm/utils';
import ApiErrorAlert from 'src/components/_common/ApiErrorAlert/ApiErrorAlert';
import PageHeader from 'src/components/_common/PageHeader/PageHeader';
import Spinner from 'src/components/_common/Spinner/Spinner';
import useApproveAttendance from 'src/hooks/attendance/useApproveAttendance';
import useAttendance from 'src/hooks/attendance/useAttendance';
import useUnapproveAttendance from 'src/hooks/attendance/useUnapproveAttendance';
import ToastsContext from 'src/hooks/useToasts';
import useLoggedInUser from 'src/hooks/user/useLoggedInUser';
import { useSupervisorAttendancePageData } from './queries';

export default function SupervisorAttendancePage() {
  const { formatMessage } = useIntl();
  const { classId, userId } = useParams<{ classId: string; userId: string }>();
  const classIdNumber = Number(classId ?? 0);
  const studentUserId = Number(userId ?? 0);
  const { data: loggedInUser } = useLoggedInUser();
  const query = useSupervisorAttendancePageData(classIdNumber, studentUserId);
  const attendanceQuery = useAttendance(classIdNumber, studentUserId);
  const approveMutation = useApproveAttendance();
  const unapproveMutation = useUnapproveAttendance();
  const { setShowSuccessToast } = ToastsContext.useContainer();
  const onSuccess = () => setShowSuccessToast(true);

  if (!classId || !userId) return <Spinner />;
  if (query.isLoading || attendanceQuery.isLoading) return <Spinner />;
  if (query.isError || attendanceQuery.isError) {
    return (
      <ApiErrorAlert
        error={query.error ?? attendanceQuery.error!}
        message={formatMessage({ id: 'attendance-page-error' })}
      />
    );
  }

  const { student, signingForm } = query.data;
  const locked = signingForm?.isLocked ?? false;
  const canApprove = !!loggedInUser && isPrimaryAdvisor(loggedInUser, student.studentReferences);
  const mutationError = approveMutation.error ?? unapproveMutation.error;

  return (
    <>
      <PageHeader
        title={formatMessage({ id: 'attendance-page-title' })}
        subTitle={student.displayName}
      />
      {mutationError && (
        <ApiErrorAlert
          error={mutationError}
          message={formatMessage({ id: 'attendance-approve-error' })}
        />
      )}
      <AttendanceList
        entries={attendanceQuery.data}
        mode="supervisor"
        readOnly={locked || !canApprove}
        onApprove={attendanceId => approveMutation.mutate({ classId: classIdNumber, studentUserId, attendanceId }, { onSuccess })}
        onUnapprove={attendanceId => unapproveMutation.mutate({ classId: classIdNumber, studentUserId, attendanceId }, { onSuccess })}
        renderRowDetails={attendanceId => (
          <AttendanceLog
            classId={classIdNumber}
            studentUserId={studentUserId}
            attendanceId={attendanceId}
            loggedInUserId={loggedInUser?.id}
          />
        )}
      />
    </>
  );
}
